/**
 * La caméra qui disparaît en pleine séance (pur) : vers quel appareil la salle bascule-t-elle d'elle-même ?
 *
 * LiveKit rattrape seul un micro débranché (`restartOnDefaultAudioDevice`), pas une caméra : à la fin de la piste,
 * il la redemande avec ses contraintes d'origine (`restartTrack()`). Or une caméra choisie, dans la salle ou au test
 * d'avant séance, y est fixée par son identifiant exact (`switchActiveDevice`, `exact` par défaut) : débranchée, elle
 * est redemandée, refusée, et la piste reste muette. Le tuteur devait rouvrir la liste et choisir à la main (weekly du
 * 03/11, une caméra externe débranchée en plein live) ; l'élève aussi, la salle étant la même pour tous.
 *
 * La règle :
 *  - l'appareil actif est encore là : rien, sauf si c'est un repli et que la caméra choisie est revenue (rebranchée),
 *    qu'on reprend ;
 *  - il n'y est plus : la caméra choisie si elle est là, sinon celle du système (`default`), sinon la première ;
 *  - aucune caméra : rien, il n'y a pas de quoi basculer.
 */

export type CameraFallbackInput = {
    /** L'appareil que la salle utilise : la caméra filmée, ou `room.getActiveDevice("videoinput")`. */
    activeId: string | null | undefined
    /** La caméra choisie (au test d'avant séance ou dans la salle), si l'on en a une. */
    chosenId: string | null | undefined
    /** Les caméras que le navigateur liste maintenant, alias système compris (`default`). */
    availableIds: string[]
    /** L'appareil actif est-il un repli posé par cette règle ? */
    onFallback: boolean
}

export function cameraFallback(input: CameraFallbackInput): string | null {
    const { activeId, chosenId, availableIds, onFallback } = input
    if (availableIds.length === 0) return null
    const has = (id: string | null | undefined): id is string => id != null && id !== "" && availableIds.includes(id)
    if (activeId == null || activeId === "" || has(activeId)) {
        // Rebranchée : la caméra choisie revient, le repli lui rend la place.
        return onFallback && has(chosenId) && chosenId !== activeId ? chosenId : null
    }
    if (has(chosenId)) return chosenId
    if (has("default")) return "default"
    return availableIds.find((id) => id !== "communications") ?? availableIds[0]
}
